import { isCssBackgroundImage } from '../../helpers.js';

interface BackgroundGradientFieldProps {
  /** Current `style.backgroundGradient` value ('' when unset). */
  value: string;
  /** Called with the raw text, or `undefined` when the field is emptied. */
  onChange: (value: string | undefined) => void;
  label?: string;
  /** Unique id so the label is clickable — the inspector renders several fields. */
  id?: string;
  /** Extra guidance shown under the field while the value looks usable. */
  hint?: string;
}

/**
 * Free-text control for `block.style.backgroundGradient`.
 *
 * The property is a whole CSS `background-image` value (`linear-gradient(…)`,
 * usually, but `url(…)` is equally valid), and the renderers emit it verbatim —
 * web as `background-image`, email alongside `background-color` so a client that
 * cannot paint a gradient still shows the solid colour. The value is therefore
 * kept as text and never parsed or rebuilt here.
 *
 * A value the browser would silently drop is called out instead of being
 * previewed as if it worked: the swatch only appears for a value the current
 * browser accepts, and the hint is replaced by a warning when it does not.
 */
export function BackgroundGradientField({
  value,
  onChange,
  label = 'Background gradient',
  id = 'block-bg-gradient',
  hint,
}: BackgroundGradientFieldProps) {
  const usable = isCssBackgroundImage(value);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <label className="field-label" htmlFor={id}>
          {label}
        </label>
        {value && (
          <button
            type="button"
            className="text-muted hover:text-ink-soft text-xs"
            onClick={() => onChange(undefined)}
          >
            Clear
          </button>
        )}
      </div>

      <input
        id={id}
        title={label}
        type="text"
        className="field-input font-mono"
        placeholder="linear-gradient(135deg, #6366f1, #ec4899)"
        value={value}
        onChange={(e) => onChange(e.target.value || undefined)}
      />

      {value && usable && (
        <div
          aria-hidden="true"
          className="border-line mt-1.5 h-7 w-full rounded-lg border"
          style={{ backgroundImage: value }}
        />
      )}

      {value && !usable ? (
        <p className="text-warn mt-1 text-xs">
          Not a CSS gradient — the browser would drop this. Try linear-gradient(135deg, #6366f1,
          #ec4899).
        </p>
      ) : (
        hint && <p className="text-muted mt-1 text-xs">{hint}</p>
      )}
    </div>
  );
}
